import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Button,
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Spin,
  Table,
  Tag,
  message,
  DatePicker,
  Select
} from 'antd'

import {
  AppstoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileTextOutlined,
  ReloadOutlined,
  ShopOutlined,
  CreditCardOutlined,
  TrophyOutlined,
  LineChartOutlined,
} from '@ant-design/icons'


import DashboardService from '../services/DashboardService'
import BillService from '../services/Billservice'
import { navigate } from '../components/Customs/navigation'
import dayjs from 'dayjs'
//DATE AND TIME RANGE 
const { RangePicker } = DatePicker
const { Option } = Select



const formatCurrency = (value) => {
  return `₹${Math.round(Number(value || 0)).toLocaleString('en-IN')}`
}

const formatAxisAmount = (value) => {
  const num = Number(value || 0)
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`
  if (num >= 1000) return `₹${(num / 1000).toFixed(1)}k`
  return `₹${Math.round(num)}`
}
const EMPTY_PAYMENT_SUMMARY = {
cash: 0,
upi: 0,
card: 0,
other: 0,
}

const normalizePaymentMethod = (value) => {
  const method = String(value || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '')

  if (method === 'cash') return 'cash'

  if (
    method === 'upi' ||
    method === 'gpay' ||
    method === 'phonepe' ||
    method === 'paytm'
  ) return 'upi'

  if (
    method === 'card' ||
    method === 'creditcard' ||
    method === 'debitcard'
  ) return 'card'

  return 'other'
}




const normalizePaymentSummary = (rawSummary) => {
if (!rawSummary) return { ...EMPTY_PAYMENT_SUMMARY }
if (Array.isArray(rawSummary)) {
return rawSummary.reduce((acc, row) => {
const key = normalizePaymentMethod(row?.payment_method || row?.method)
const amount = Number(row?.bill_total ?? row?.amount ?? row?.total ?? 0)
acc[key] += Number.isFinite(amount) ? amount : 0
return acc
}, { ...EMPTY_PAYMENT_SUMMARY })
}
const summary = { ...EMPTY_PAYMENT_SUMMARY }
summary.cash = Number(rawSummary.cash ?? rawSummary.total_cash ?? 0)
summary.upi = Number(rawSummary.upi ?? rawSummary.total_upi ?? 0)
summary.card = Number(rawSummary.card ?? rawSummary.total_card ?? 0)
summary.other = Number(rawSummary.other ?? rawSummary.total_other ?? 0)
if (
Number.isFinite(summary.cash) &&
Number.isFinite(summary.upi) &&
Number.isFinite(summary.card) &&
Number.isFinite(summary.other)
) {
return summary
}
return { ...EMPTY_PAYMENT_SUMMARY }
}

const parsePaymentSummary = (raw) => {
  if (!raw) return null
  try {
    return typeof raw === 'string' ? JSON.parse(raw) : raw
  } catch {
    return null
  }
}

const getDrinkTotalFromDetails = (drinkDetailsRaw) => {
  if (!drinkDetailsRaw) return 0

  let drinkDetails = drinkDetailsRaw
  if (typeof drinkDetails === 'string') {
    try {
      drinkDetails = JSON.parse(drinkDetails)
    } catch {
      drinkDetails = {}
    }
  }

  const pegs = Array.isArray(drinkDetails?.pegs) ? drinkDetails.pegs : []
  const pegQtys = drinkDetails?.peg_qtys || {}
  const bottleQty = Number(drinkDetails?.bottle_qty || 0)
  const bottlePrice = Number(drinkDetails?.price_bottle || 0)

  const bottleTotal = bottleQty * bottlePrice
  const pegTotal = pegs.reduce((sum, peg) => {
    const qty = Number(pegQtys[String(peg?.id)] || 0)
    return sum + qty * Number(peg?.price || 0)
  }, 0)

  return bottleTotal + pegTotal
}

const resolveFoodSubtotal = (bill) => {
  const ps = parsePaymentSummary(bill?.payment_summary || bill?.bill_summary) || {}
  const fromSummary = Number(
    ps.food_subtotal ?? ps.foodSubtotal ?? bill?.food_subtotal ?? bill?.totals?.food_subtotal
  )
  if (Number.isFinite(fromSummary)) return fromSummary

  const items = Array.isArray(bill?.items) ? bill.items : []
  return items.reduce((sum, item) => {
    if (Number(item?.is_drink || 0) === 1) return sum
    return sum + Number(item?.price || 0) * Number(item?.qty || 0)
  }, 0)
}

const resolveDrinkSubtotal = (bill) => {
  const ps = parsePaymentSummary(bill?.payment_summary || bill?.bill_summary) || {}
  const fromSummary = Number(
    ps.drink_subtotal ?? ps.drinkSubtotal ?? bill?.drink_subtotal ?? bill?.totals?.drink_subtotal
  )
  if (Number.isFinite(fromSummary)) return fromSummary

  const items = Array.isArray(bill?.items) ? bill.items : []
  return items.reduce((sum, item) => {
    if (Number(item?.is_drink || 0) !== 1) return sum

    const detailsTotal = getDrinkTotalFromDetails(item?.drink_details)
    if (detailsTotal > 0) return sum + detailsTotal

    return sum + Number(item?.price || 0) * Number(item?.qty || 0)
  }, 0)
}
const DashboardPage = () => {

  //time and date range
const [rangeType, setRangeType] = useState('today')
const [dateRange, setDateRange] = useState(null)
const [currentTime, setCurrentTime] = useState(dayjs())


const [loading, setLoading] = useState(true)
const [summary, setSummary] = useState({
total_tables: 0,
occupied_tables: 0,
available_tables: 0,
today_revenue: 0,
closed_bills_today: 0,
open_bills: 0,
})
const [tableStatus, setTableStatus] = useState([])
const [recentBills, setRecentBills] = useState([])
const [paymentSummary, setPaymentSummary] = useState({
...EMPTY_PAYMENT_SUMMARY,
})
const [topItems, setTopItems] = useState([])
const [salesTrend, setSalesTrend] = useState([])
const [closedBills, setClosedBills] = useState([])

const dateFilters = useMemo(() => {
  const now = dayjs()

  if (rangeType === 'week') {
    return {
      startDate: now.startOf('week').format('YYYY-MM-DD HH:mm:ss'),
      endDate: now.endOf('week').format('YYYY-MM-DD HH:mm:ss'),
    }
  }

  if (rangeType === 'month') {
    return {
      startDate: now.startOf('month').format('YYYY-MM-DD HH:mm:ss'),
      endDate: now.endOf('month').format('YYYY-MM-DD HH:mm:ss'),
    }
  }

  if (rangeType === 'custom' && Array.isArray(dateRange) && dateRange.length === 2) {
    return {
      startDate: dayjs(dateRange[0]).format('YYYY-MM-DD HH:mm:ss'),
      endDate: dayjs(dateRange[1]).format('YYYY-MM-DD HH:mm:ss'),
    }
  }

  return {
    startDate: now.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
    endDate: now.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
  }
}, [rangeType, dateRange])

const trendRange = rangeType === 'month' ? '30days' : '7days'

const loadDashboard = useCallback(async () => {
try {
setLoading(true)
const [
summaryRes,
tableStatusRes,
recentBillsRes,
paymentSummaryRes,
topItemsRes,
salesTrendRes,
] = await Promise.all([
DashboardService.getSummary(dateFilters),
DashboardService.getTableStatus(),
DashboardService.getRecentBills(10, dateFilters),
DashboardService.getPaymentSummary(dateFilters),
DashboardService.getTopItems(5, dateFilters),
DashboardService.getSalesTrend(trendRange),
])
setSummary(summaryRes || {})
setTableStatus(Array.isArray(tableStatusRes) ? tableStatusRes : [])
setRecentBills(Array.isArray(recentBillsRes) ? recentBillsRes : [])
setPaymentSummary(normalizePaymentSummary(paymentSummaryRes))
setTopItems(Array.isArray(topItemsRes) ? topItemsRes : [])
setSalesTrend(Array.isArray(salesTrendRes) ? salesTrendRes : [])

const closedBillsFilters = {}
if (rangeType === 'today') {
  closedBillsFilters.date = 'today'
} else if (rangeType === 'week' || rangeType === 'month' || rangeType === 'custom') {
  if (dateFilters?.startDate) {
    closedBillsFilters.from = dayjs(dateFilters.startDate).format('YYYY-MM-DD')
  }
  if (dateFilters?.endDate) {
    closedBillsFilters.to = dayjs(dateFilters.endDate).format('YYYY-MM-DD')
  }
}
const closedBillsRes = await BillService.getClosedBills(closedBillsFilters)
setClosedBills(Array.isArray(closedBillsRes) ? closedBillsRes : [])
} catch (error) {
message.error(error?.message || 'Failed to load dashboard')
} finally {
setLoading(false)
}
}, [dateFilters, trendRange])
useEffect(() => {
loadDashboard()
}, [loadDashboard])

// changed according data and time 
useEffect(() => {
  const interval = setInterval(() => {
    setCurrentTime(dayjs())
  }, 1000)

  return () => clearInterval(interval)
}, [])

const totalPayment = useMemo(() => {
return (
Number(paymentSummary.cash || 0) +
Number(paymentSummary.upi || 0) +
Number(paymentSummary.card || 0) +
Number(paymentSummary.other || 0)
)
}, [paymentSummary])
const billColumns = [
{
title: 'Bill No',
dataIndex: 'bill_no',
key: 'bill_no',
render: (text) => <span className="username-text">{text || '-'}</span>,
},
{
title: 'Table',
dataIndex: 'table_name',
key: 'table_name',
render: (text) => text || '-',
},
{
title: 'Amount',
dataIndex: 'final_total',
key: 'final_total',
render: (value) => <strong>{formatCurrency(value)}</strong>,
},
{
title: 'Payment',
dataIndex: 'payment_method',
key: 'payment_method',
render: (value) => {
const method = String(value || '').toLowerCase()
let color = 'default'
if (method === 'cash') color = 'green'
if (method === 'upi') color = 'blue'
if (method === 'card') color = 'purple'
return <Tag color={color}>{value || 'N/A'}</Tag>
},
},
{
title: 'Status',
dataIndex: 'status',
key: 'status',
render: (value) => {
const isClosed = String(value || '').toLowerCase() === 'closed'
return <Tag color={isClosed ? 'green' : 'orange'}>{value || '-'}</Tag>
},
},
]
const topItemColumns = [
{
title: 'Item',
dataIndex: 'name',
key: 'name',
render: (text) => <span className="username-text">{text}</span>,
},
{
title: 'Qty',
dataIndex: 'total_qty',
key: 'total_qty',
width: 100,
render: (value) => Number(value || 0),
},
{
title: 'Amount',
dataIndex: 'total_amount',
key: 'total_amount',
width: 140,
render: (value) => formatCurrency(value),
},
]
const trendMax = useMemo(() => {
return Math.max(0, ...salesTrend.map((item) => Number(item.total_sales || 0)))
}, [salesTrend])

const subtotalChartData = useMemo(() => {
  const foodSubtotal = closedBills.reduce((sum, bill) => sum + resolveFoodSubtotal(bill), 0)
  const drinkSubtotal = closedBills.reduce((sum, bill) => sum + resolveDrinkSubtotal(bill), 0)
  const total = foodSubtotal + drinkSubtotal
  return { foodSubtotal, drinkSubtotal, total }
}, [closedBills])

const pieData = useMemo(() => {
  return [
    { key: 'food', label: 'Food Subtotal', value: subtotalChartData.foodSubtotal, color: '#1677ff' },
    { key: 'drink', label: 'Drink Subtotal', value: subtotalChartData.drinkSubtotal, color: '#fa8c16' },
  ]
}, [subtotalChartData])

const pieSegments = useMemo(() => {
  const radius = 54
  const safeTotal = Math.max(subtotalChartData.total, 0)

  if (safeTotal <= 0) {
    return pieData.map((item) => ({ ...item, percent: 0, pathD: '' }))
  }

  let startAngle = -Math.PI / 2
  return pieData.map((item) => {
    const percent = (item.value / safeTotal) * 100
    const sweep = (item.value / safeTotal) * Math.PI * 2
    const endAngle = startAngle + sweep
    const largeArcFlag = sweep > Math.PI ? 1 : 0

    const x1 = 70 + radius * Math.cos(startAngle)
    const y1 = 70 + radius * Math.sin(startAngle)
    const x2 = 70 + radius * Math.cos(endAngle)
    const y2 = 70 + radius * Math.sin(endAngle)

    const segment = {
      ...item,
      percent,
      pathD:
        item.value <= 0
          ? ''
          : `M 70 70 L ${x1.toFixed(3)} ${y1.toFixed(3)} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2.toFixed(3)} ${y2.toFixed(3)} Z`,
    }
    startAngle = endAngle
    return segment
  })
}, [pieData, subtotalChartData.total])

const lineChartData = useMemo(() => {
  const grouped = {}
  const sameDayTimeSeries =
    rangeType === 'today' ||
    (rangeType === 'custom' &&
      Array.isArray(dateRange) &&
      dateRange.length === 2 &&
      dayjs(dateRange[0]).isSame(dayjs(dateRange[1]), 'day'))

  closedBills.forEach((bill) => {
    const closedAt = bill?.closed_at ? dayjs(bill.closed_at) : null
    const bucketTime = closedAt
      ? sameDayTimeSeries
        ? closedAt.startOf('hour')
        : closedAt.startOf('day')
      : dayjs(0)
    const bucketKey = bucketTime.valueOf()

    if (!grouped[bucketKey]) {
      grouped[bucketKey] = {
        label: sameDayTimeSeries ? bucketTime.format('HH:mm') : bucketTime.format('DD MMM'),
        rawDate: bucketKey,
        food: 0,
        drink: 0,
      }
    }
    grouped[bucketKey].food += resolveFoodSubtotal(bill)
    grouped[bucketKey].drink += resolveDrinkSubtotal(bill)
  })

  return Object.values(grouped)
    .sort((a, b) => a.rawDate - b.rawDate)
    .map(({ label, food, drink }) => ({ label, food, drink }))
}, [closedBills, rangeType, dateRange])

const lineChartScale = useMemo(() => {
  const maxValue = Math.max(
    0,
    ...lineChartData.map((item) => Math.max(Number(item.food || 0), Number(item.drink || 0)))
  )
  return maxValue > 0 ? maxValue : 1
}, [lineChartData])

const yAxisTicks = useMemo(() => {
  const levels = 4
  return Array.from({ length: levels + 1 }, (_, index) => {
    const ratio = index / levels
    return {
      value: lineChartScale * (1 - ratio),
      y: 16 + ratio * (220 - 16 - 24),
    }
  })
}, [lineChartScale])

const createLinePath = (key) => {
  if (!lineChartData.length) return ''
  const width = 620
  const height = 220
  const leftPad = 18
  const rightPad = 18
  const topPad = 16
  const bottomPad = 24
  const innerWidth = width - leftPad - rightPad
  const innerHeight = height - topPad - bottomPad
  const hasMultiplePoints = lineChartData.length > 1
  const stepX = hasMultiplePoints ? innerWidth / (lineChartData.length - 1) : 0
  const points = lineChartData.map((row, index) => {
    const x = hasMultiplePoints ? leftPad + index * stepX : leftPad + innerWidth / 2
    const y = topPad + (1 - Number(row[key] || 0) / lineChartScale) * innerHeight
    return [x, y]
  })
  return points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`).join(' ')
}

const foodLinePath = useMemo(() => createLinePath('food'), [lineChartData, lineChartScale])
const drinkLinePath = useMemo(() => createLinePath('drink'), [lineChartData, lineChartScale])
const lineChartPoints = useMemo(() => {
  if (!lineChartData.length) return []

  const width = 620
  const height = 220
  const leftPad = 18
  const rightPad = 18
  const topPad = 16
  const bottomPad = 24
  const innerWidth = width - leftPad - rightPad
  const innerHeight = height - topPad - bottomPad
  const hasMultiplePoints = lineChartData.length > 1
  const stepX = hasMultiplePoints ? innerWidth / (lineChartData.length - 1) : 0

  return lineChartData.map((row, index) => {
    const x = hasMultiplePoints ? leftPad + index * stepX : leftPad + innerWidth / 2
    const foodY = topPad + (1 - Number(row.food || 0) / lineChartScale) * innerHeight
    const drinkY = topPad + (1 - Number(row.drink || 0) / lineChartScale) * innerHeight
    return { x, foodY, drinkY }
  })
}, [lineChartData, lineChartScale])

const isHourWiseTrend =
  rangeType === 'today' ||
  (rangeType === 'custom' &&
    Array.isArray(dateRange) &&
    dateRange.length === 2 &&
    dayjs(dateRange[0]).isSame(dayjs(dateRange[1]), 'day'))
return (
<div className=" user-master-page">
<div className="user-page-header">
<div className="user-page-header-left">
<div className="user-page-icon">
<AppstoreOutlined />
</div>
<div>
<h2 className="user-page-title">Dashboard</h2>
<p className="user-page-subtitle">
Live overview of tables, bills, sales, and payment activity.
</p>
</div>
</div>
<div className="user-page-header-actions"

style={{
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    alignItems: 'center',
  }}
  >
{/* Live Date Time */}
<div
  style={{
    background: '#f0f5ff',
    padding: '8px 14px',
    borderRadius: '8px',
    fontWeight: 600,
    color: '#1677ff',
    border: '1px solid #d6e4ff',
    minWidth: '230px',
    textAlign: 'center',
    fontSize: '14px',
  }}
>
  {currentTime.format('DD MMM YYYY hh:mm:ss A')}
</div>

{/* Range Select */}
<Select
  value={rangeType}
  onChange={setRangeType}
  style={{ width: 160 }}
>
  <Option value="today">Today</Option>
  <Option value="week">This Week</Option>
  <Option value="month">This Month</Option>
  <Option value="custom">Custom Date</Option>
</Select>

{/* Custom Date + Time Range */}
{rangeType === 'custom' && (
  <RangePicker
    showTime={{
      format: 'hh:mm:ss A',
    }}
    value={dateRange}
    onChange={setDateRange}
    format="DD-MM-YYYY hh:mm:ss A"
    style={{
      borderRadius: '8px',
      width: 340,
    }}
    placeholder={['Start Date Time', 'End Date Time']}
  />
)}




<Button
icon={<ReloadOutlined />}
className="secondary-btn"
onClick={loadDashboard}
loading={loading}
>
Refresh
</Button>
</div>
</div>
{loading ? (
<div className="page-empty">
<Spin size="large" />
</div>
) : (
<>
{/* Row 1 */}
<Row gutter={[16, 16]} className="user-stats-row">
<Col xs={24} sm={12} lg={8} xl={4}>
<div className="stats-card">
<div className="stats-card-icon blue">
<ShopOutlined />
</div>
<div>
<div className="stats-card-label">Total Tables</div>
<div className="stats-card-value">{summary.total_tables || 0}</div>
</div>
</div>
</Col>
<Col xs={24} sm={12} lg={8} xl={4}>
<div className="stats-card">
<div className="stats-card-icon red">
<ClockCircleOutlined />
</div>
<div>
<div className="stats-card-label">Occupied</div>
<div className="stats-card-value">{summary.occupied_tables || 0}</div>
</div>
</div>
</Col>
<Col xs={24} sm={12} lg={8} xl={4}>
<div className="stats-card">
<div className="stats-card-icon green">
<CheckCircleOutlined />
</div>
<div>
<div className="stats-card-label">Available</div>
<div className="stats-card-value">{summary.available_tables || 0}</div>
</div>
</div>
</Col>
<Col xs={24} sm={12} lg={8} xl={4}>
<div className="stats-card">
<div className="stats-card-icon blue">
<DollarOutlined />
</div>
<div>
<div className="stats-card-label">Today Revenue</div>
<div className="stats-card-value">{formatCurrency(summary.today_revenue)}</div>
</div>
</div>
</Col>
<Col xs={24} sm={12} lg={8} xl={4}>
<div className="stats-card">
<div className="stats-card-icon green">
<FileTextOutlined />
</div>
<div>
<div className="stats-card-label">Closed Bills</div>
<div className="stats-card-value">{summary.closed_bills_today || 0}</div>
</div>
</div>
</Col>
<Col xs={24} sm={12} lg={8} xl={4}>
<div className="stats-card">
<div className="stats-card-icon red">
<AppstoreOutlined />
</div>
<div>
<div className="stats-card-label">Open Bills</div>
<div className="stats-card-value">{summary.open_bills || 0}</div>
</div>
</div>
</Col>
</Row>
{/* Row 2 */}
<Row gutter={[16, 16]}>
<Col xs={24} lg={12}>
<div className="page-card table-card" style={{ height: '100%' }}>
<div className="table-card-header">
<div>
<h3>Live Table Status</h3>
<p>Current table availability and open bill count</p>
</div>
</div>
{tableStatus.length === 0 ? (
<div className="page-empty">
<Empty description="No table data found" />
</div>
) : (
<div className="dashboard-table-grid">
{tableStatus.map((table) => {
const isOccupied = String(table.status || '').toLowerCase() === 'occupied'
return (
<Card
key={table.id}
className="dashboard-table-tile"
hoverable
onClick={() => navigate('/manage-bills')}
>
<div className="dashboard-table-top">
<div className="dashboard-table-name">{table.name}</div>
<Tag color={isOccupied ? 'red' : 'green'}>
{table.status || 'unknown'}
</Tag>
</div>
<div className="dashboard-table-meta">
<div>
<span className="dashboard-meta-label">Zone:</span>{' '}
<span>{table.zone || '-'}</span>
</div>
<div>
<span className="dashboard-meta-label">Open Bills:</span>{' '}
<strong>{Number(table.open_bill_count || 0)}</strong>
</div>
</div>
</Card>
)
})}
</div>
)}
</div>
</Col>
<Col xs={24} lg={12}>
<div className="page-card table-card" style={{ height: '100%' }}>
<div className="table-card-header">
<div>
<h3>Recent Bills</h3>
<p>Latest 10 bills</p>
</div>
</div>
{recentBills.length === 0 ? (
<div className="page-empty">
<Empty description="No recent bills found" />
</div>
) : (
<div className="common-table-wrap">
<Table
rowKey="id"
columns={billColumns}
dataSource={recentBills}
pagination={false}
size="middle"
/>
</div>
)}
</div>
</Col>
</Row>
{/* Row 3 */}
<Row gutter={[16, 16]}  style={{ marginTop: 16 }}>
<Col xs={24} lg={12}>
<div className="page-card table-card" style={{ height: '100%' }}>
<div className="table-card-header">
<div>
<h3>Payment Summary</h3>
<p>Today's collection by payment method</p>
</div>
</div>
<div className="payment-summary-grid">
<div className="payment-box">
<div className="payment-box-title">Cash</div>
<div className="payment-box-value">{formatCurrency(paymentSummary.cash)}</div>
</div>
<div className="payment-box">
<div className="payment-box-title">UPI</div>
<div className="payment-box-value">{formatCurrency(paymentSummary.upi)}</div>
</div>
<div className="payment-box">
<div className="payment-box-title">Card</div>
<div className="payment-box-value">{formatCurrency(paymentSummary.card)}</div>
</div>
<div className="payment-box">
<div className="payment-box-title">Other</div>
<div className="payment-box-value">{formatCurrency(paymentSummary.other)}</div>
</div>
</div>
<div className="payment-total-row">
<span>Total</span>
<strong>{formatCurrency(totalPayment)}</strong>
</div>
</div>
</Col>
<Col xs={24} lg={12}>
<div className="page-card table-card" style={{ height: '100%' }}>
<div className="table-card-header">
<div>
<h3>Top Selling Items</h3>
<p>Best performing items for today</p>
</div>
</div>
{topItems.length === 0 ? (
<div className="page-empty">
<Empty description="No item sales found" />
</div>
) : (
<div className="common-table-wrap">
<Table
rowKey={(record, index) => `${record.name}_${index}`}
columns={topItemColumns}
dataSource={topItems}
pagination={false}
size="middle"
/>
</div>
)}
</div>
</Col>
</Row>





{/* Row 4 */}
<Row gutter={[16, 16]} align="stretch"  justify="center"  style={{ marginTop: 16 }}>
   {/* Pie Chart Card */}
  {/* <Col xs={24} lg={10} style={{ display: 'flex' }}>
    <div
      className="page-card table-card"
      style={{
        width: '100%',
        minHeight: 340,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div className="table-card-header">
        <div>
          <h3>Food vs Drink Split</h3>
        </div>
      </div>

      <div style={{ flex: 1 }}>
        {subtotalChartData.total <= 0 ? (
          <div className="page-empty">
            <Empty description="No subtotal data found" />
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '180px 1fr',
              gap: 16,
              alignItems: 'center',
              height: '100%',
            }}
          >
           <svg
            width="180"
            height="150"
            viewBox="0 0 140 140"
            aria-label="Food and drink subtotal pie chart"
          >
            
            {pieSegments.length === 1 ||
            pieSegments.some((s) => s.percent === 100) ? (
              <circle
                cx="70"
                cy="70"
                r="50"
                fill={pieSegments.find((s) => s.value > 0)?.color || '#1677ff'}
              />
            ) : (
              pieSegments.map((segment) => (
                <path
                  key={segment.key}
                  d={segment.pathD}
                  fill={segment.color}
                />
              ))
            )}
          </svg>

            <div>
              {pieSegments.map((segment) => (
                <div
                  key={segment.key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 10,
                  }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: segment.color,
                        display: 'inline-block',
                      }}
                    />
                    {segment.label}
                  </span>

                  <strong>
                    {formatCurrency(segment.value)} (
                    {segment.percent.toFixed(1)}%)
                  </strong>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  </Col>   */}

  {/* Line Chart Card */}
<Col xs={24} lg={10} style={{ display: 'flex' }}>
  <div
    className="page-card table-card"
    style={{
      width: '100%',
      minHeight: 340,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'visible',
    }}
  >
    <div className="table-card-header">
      <div>
        <h3>Food Trend</h3>
        <p>{isHourWiseTrend ? 'Hour-wise line chart records' : 'Day-wise line chart records'}</p>
      </div>
    </div>

    <div style={{ flex: 1, overflow: 'visible' }}>
      {lineChartData.length === 0 ? (
        <div className="page-empty">
          <Empty description="No trend data found" />
        </div>
      ) : (
        (() => {
          // ✅ CHART CONFIG - Adjusted for Y-axis labels
          const Y_AXIS_LABEL_WIDTH = 45
          const CHART_START_X = Y_AXIS_LABEL_WIDTH + 5
          const CHART_END_X = 580
          const CHART_WIDTH = CHART_END_X - CHART_START_X
          const CHART_TOP = 16
          const CHART_BOTTOM = 190

          // Calculate X positions for data points
          const xStep = CHART_WIDTH / (lineChartData.length - 1 || 1)

          const updatedPoints = lineChartPoints.map((point, index) => ({
            ...point,
            x: CHART_START_X + index * xStep,
          }))

          return (
            <>
              <div style={{ overflowX: 'auto', overflowY: 'visible' }}>
                <svg
                  width="100%"
                  height="220"
                  viewBox="0 0 650 240"
                  preserveAspectRatio="xMidYMid meet"
                  aria-label="Food line chart"
                  style={{ overflow: 'visible' }}
                >
                  {/* Y-axis */}
                  <line
                    x1={CHART_START_X}
                    y1={CHART_TOP}
                    x2={CHART_START_X}
                    y2={CHART_BOTTOM}
                    stroke="#d9d9d9"
                    strokeWidth="1"
                  />

                  {/* X-axis */}
                  <line
                    x1={CHART_START_X}
                    y1={CHART_BOTTOM}
                    x2={CHART_END_X}
                    y2={CHART_BOTTOM}
                    stroke="#d9d9d9"
                    strokeWidth="1"
                  />

                  {/* Y grid lines + labels */}
                  {yAxisTicks.map((tick, index) => (
                    <g key={`y-axis-${index}`}>
                      <line
                        x1={CHART_START_X}
                        y1={tick.y}
                        x2={CHART_END_X}
                        y2={tick.y}
                        stroke="#f0f0f0"
                        strokeWidth="1"
                      />

                      <text
                        x={Y_AXIS_LABEL_WIDTH - 5}
                        y={tick.y + 4}
                        textAnchor="end"
                        fontSize="10"
                        fill="#595959"
                        fontWeight="500"
                      >
                        {formatAxisAmount(tick.value)}
                      </text>
                    </g>
                  ))}

                  {/* Food line */}
                  {foodLinePath && (
                    <path
                      d={(() => {
                        if (!lineChartData.length) return ''

                        const maxValue = Math.max(
                          ...lineChartData.map((d) => d.food)
                        )

                        const minValue = Math.min(
                          ...lineChartData.map((d) => d.food),
                          0
                        )

                        const yRange = maxValue - minValue

                        const segments = lineChartData.map((item, idx) => {
                          const x = CHART_START_X + idx * xStep

                          const y =
                            yRange === 0
                              ? CHART_BOTTOM
                              : CHART_BOTTOM -
                                ((item.food - minValue) / yRange) *
                                  (CHART_BOTTOM - CHART_TOP)

                          return `${idx === 0 ? 'M' : 'L'} ${x} ${Math.max(
                            CHART_TOP,
                            Math.min(CHART_BOTTOM, y)
                          )}`
                        })

                        return segments.join(' ')
                      })()}
                      fill="none"
                      stroke="#1677ff"
                      strokeWidth="2.5"
                    />
                  )}

                  {/* Food points */}
                  {updatedPoints.map((point, index) => (
                    <g key={`line-point-${index}`}>
                      {point.foodY !== null && (
                        <circle
                          cx={point.x}
                          cy={Math.max(
                            CHART_TOP,
                            Math.min(CHART_BOTTOM, point.foodY)
                          )}
                          r="4"
                          fill="#1677ff"
                          stroke="#fff"
                          strokeWidth="1.5"
                        />
                      )}

                      {/* X-axis label */}
                      <text
                        x={point.x}
                        y={CHART_BOTTOM + 18}
                        textAnchor="middle"
                        fontSize="10"
                        fill="#8c8c8c"
                      >
                        {lineChartData[index]?.label || ''}
                      </text>
                    </g>
                  ))}
                </svg>
              </div>

              {/* Bottom legend */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 10,
                  flexWrap: 'wrap',
                  marginTop: 12,
                  paddingTop: 8,
                  borderTop: '1px solid #f0f0f0',
                }}
              >
                {lineChartData.map((item) => (
                  <div key={item.label} style={{ minWidth: 90 }}>
                    <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                      {item.label}
                    </div>

                    <div style={{ fontSize: 12, color: '#1677ff' }}>
                      Food: {formatCurrency(item.food)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )
        })()
      )}
    </div>
  </div>
</Col>
</Row>


 {/*<Row gutter={[12, 12]} style={{ marginTop: 12 }}>
  <Col span={24}>
    <div
      className="page-card table-card"
      style={{ paddingTop: 16, paddingBottom: 16 }}
    >
      <div
        className="table-card-header"
        style={{ marginBottom: 14 }}
      >
        <div>
          <h3>Sales Trend</h3>
          <p>Last 7 days sales trend</p>
        </div>
      </div>

      {salesTrend.length === 0 ? (
        <div className="page-empty">
          <Empty description="No sales trend found" />
        </div>
      ) : (
        <div
          className="sales-trend-list"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}
        >
          {salesTrend.map((item, index) => {
            const total = Number(item.total_sales || 0)

            const percent =
              trendMax > 0
                ? Math.round((total / trendMax) * 100)
                : 0

            return (
              <div
                className="sales-trend-row"
                key={`${item.sales_date}_${index}`}
                style={{
                  paddingTop: 6,
                  paddingBottom: 6,
                }}
              >
                <div className="sales-trend-date">
                <LineChartOutlined />{' '}
                {item.sales_date
                  ? dayjs(item.sales_date).format('DD-MM-YYYY')
                  : '-'}
              </div>

                <div className="sales-trend-bar">
                  <Progress
                    percent={percent}
                    showInfo={false}
                  />
                </div>

                <div className="sales-trend-value">
                  {formatCurrency(total)}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  </Col>
</Row> */}
</>
)}
</div>
)
}
export default DashboardPage



